// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — client calls that exist only for the /pot ("POT classic") screens. Everything that
// already has a function in web/src/api/client.ts is imported from there; this file carries the
// NEW endpoints added by this feature plus small shape helpers. Keep it additive — several
// screens are built in parallel against it.
// D-POT-5: the JSON request helper moved to potShared (the OwlRx Plan of Treatment form uses it too).
import { potRequest as req } from '../carerx/carerxPotRequest'
import type { PotPatient, PotPrefill } from '../carerx/carerxPotPrefill'

// ── Dashboard (GET carerx/pot/classic-dashboard) ─────────────────────────────────────────────
export type PotSignatureBadge = 'not_sent' | 'awaiting_physician' | 'signed' | 'declined' | 'voided'
export interface PotDashboardRow {
  visit_id: string
  patient_id: string
  patient_name: string
  patient_initials: string
  dob: string | null
  mrn: string | null
  drug_name: string | null
  diagnosis: string | null
  nurse_id: string | null
  nurse_name: string | null
  status: 'scheduled' | 'claimed' | 'completed' | 'cancelled'
  form_status: 'none' | 'draft' | 'completed'
  created_at: string
  completed_at: string | null
  worked_seconds: number | null
  signature: PotSignatureBadge
  envelope_id: string | null
  document_id: string | null
  void_reason: string | null
}
export interface PotDashboard {
  in_progress: PotDashboardRow[]
  completed: PotDashboardRow[]
  voided: PotDashboardRow[]
  counts: { in_progress: number; completed: number; voided: number }
  can_void: boolean
}
export const getPotClassicDashboard = () => req<PotDashboard>('GET', '/carerx/pot/classic-dashboard')

// ── Stats (GET carerx/pot/stats) — owner 2026-10-29: every total is counted on the server over EVERY visit in
// scope; the screens below summed the dashboard's newest 500 visits in the browser and went wrong past 500. ──
export type PotStatsGroup = 'nurse' | 'patient' | 'drug' | 'diagnosis'
export interface PotStatsQuery {
  from?: string | null; to?: string | null; nurse?: string[]; q?: string
  group?: PotStatsGroup[]; bucket?: 'day' | 'week' | 'month'; detail?: boolean
}
export interface PotStatsTotals {
  completed: number; signed: number; sent: number; declined: number; voided: number
  awaiting: number; active_drafts: number; in_progress: number; completed_visits: number; voided_visits: number
  overdue_unsigned_30d: number; stale_awaiting_7d: number
  avg_draft_to_complete_hrs: number | null; avg_complete_to_sent_hrs: number | null
  worked: { avg_min: number | null; min_min: number | null; max_min: number | null; n: number }
}
export interface PotStatsGroupRow {
  key: string; name?: string | null
  visits: number; visits_active: number; patients: number
  completed: number; signed: number; pending: number; awaiting: number; completed_in_range: number
  worked_avg_min: number | null; worked_min_min: number | null; worked_max_min: number | null
  last_completed_at: string | null; last_activity_at: string | null
  // group=patient&detail=1 (the nurse roster)
  patient_name?: string | null; first_name?: string | null; last_name?: string | null; mrn?: string | null; dob?: string | null
  last_status?: string | null; last_diagnosis?: string | null
}
export interface PotStats {
  totals: PotStatsTotals
  volume: { period: string; count: number }[] | null
  groups: Partial<Record<PotStatsGroup, PotStatsGroupRow[]>>
}
export function potStatsQs(q: PotStatsQuery): string {
  const u = new URLSearchParams()
  if (q.from) u.set('from', q.from)
  if (q.to) u.set('to', q.to)
  if (q.nurse?.length) u.set('nurse', q.nurse.join(','))
  if (q.q?.trim()) u.set('q', q.q.trim())
  if (q.group?.length) u.set('group', q.group.join(','))
  if (q.bucket) u.set('bucket', q.bucket)
  if (q.detail) u.set('detail', '1')
  return u.toString()
}
export const getPotStats = (q: PotStatsQuery) => req<PotStats>('GET', `/carerx/pot/stats?${potStatsQs(q)}`)
export const potStatsKey = (q: PotStatsQuery) => ['pot', 'stats', potStatsQs(q)] as const
/** The ISO start of a "last N days" window (null = all time), day-aligned so the query key is stable. */
export function potSinceDays(days: number | null, now = Date.now()): string | null {
  if (days === null) return null
  const d = new Date(now - days * 86_400_000); d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

// ── Void / unvoid (PUT carerx/visits/{id}/void) ───────────────────────────────────────────────
export const voidPotVisit = (visitId: string, reason: string) =>
  req<{ ok: boolean; status: string }>('PUT', `/carerx/visits/${visitId}/void`, { reason })

// ── Schema selection for the classic form ─────────────────────────────────────────────────────
/** The classic 14-section / ~196-field instrument seeded by migration 449. */
export const POT_CLASSIC_SCHEMA = 'pot_classic_v1'

// ── Agent A: POT classic form screens (PotDashboard / PotNew / PotForm) ──────────────────────
// The classic form rides the EXISTING pot-form contract (CareRxPotFormFunctions.cs): GET returns
// schema + draft + a Pioneer-sourced patient projection; PUT upserts the one draft (sections and
// section_status are JSON strings); POST complete freezes it behind the nurse signature. The only
// difference is `?schema=pot_classic_v1`, which the backend reads to pick the classic instrument
// (migration 449) for the draft row. It is passed on every call so a draft is never created
// against the wrong schema if the backend honours it on PUT as well as GET.
export type PotClassicPatient = PotPatient
export interface PotClassicFormRow {
  id: string
  version: number
  status: string
  sections: string | null
  section_status: string | null
  attestation: string | null
  content_hash: string | null
  updated_at: string
  completed_at: string | null
  worked_seconds?: number | null
  /** D-CRX-89: the protocols this POT was written against (pinned at authorship), JSON. */
  pinned_refs?: string | null
}
export interface PotClassicFormResponse {
  visit_id: string
  visit_status: string
  schema: { id: string; code: string; version: number; scope: string; definition: unknown } | null
  form: PotClassicFormRow | null
  patient: PotClassicPatient | null
  /** Read-only enrollment/fill facts for seeding a FRESH draft (CareRxPotFormFunctions GET). */
  prefill?: PotPrefill | null
}
const classicQs = `?schema=${POT_CLASSIC_SCHEMA}`
export const getPotClassicForm = (visitId: string) =>
  req<PotClassicFormResponse>('GET', `/carerx/visits/${visitId}/pot-form${classicQs}`)
export const putPotClassicForm = (visitId: string, body: { sections: string; section_status: string; base_updated_at?: string | null }) =>
  req<{ id: string; version: number; status: string; updated_at: string; worked_seconds?: number }>('PUT', `/carerx/visits/${visitId}/pot-form${classicQs}`, body)
export const completePotClassicForm = (visitId: string, body: { signature_data_url: string }) =>
  req<{ id: string; version: number; status: string; content_hash: string }>('POST', `/carerx/visits/${visitId}/pot-form/complete${classicQs}`, body)

// ── Agent C (Nurses / Nurse profile / Labs / Re-careplan / Help / My account) ─────────────────
/** GET carerx/ai/consent — the server also returns `version` + `at` (CareRxAiFunctions.cs), which the
 *  client.ts type drops; My Account shows "Accepted on <date>" so it needs them. */
export interface PotAiConsent { consented: boolean; version?: string; at?: string }
export const getPotAiConsent = () => req<PotAiConsent>('GET', '/carerx/ai/consent')

/** Per-nurse numbers (the ASP app's patientsAssigned / potsCompleted / visits). Owner 2026-10-29: counted on
 *  the server (GET carerx/pot/stats?group=nurse) — this used to roll up the dashboard's newest 500 visits.
 *  Keyed by `nurse_id` (the login id), lower-case. */
export interface PotNurseStats { patients: number; pots: number; signed: number; pending: number; visits: number }
export const EMPTY_NURSE_STATS: PotNurseStats = { patients: 0, pots: 0, signed: 0, pending: 0, visits: 0 }
export function nurseStatsOf(rows: PotStatsGroupRow[] | undefined): Map<string, PotNurseStats> {
  return new Map((rows ?? []).map(r => [r.key.toLowerCase(), { patients: r.patients, pots: r.completed, signed: r.signed, pending: r.pending, visits: r.visits }]))
}
export const NURSE_STATS: PotStatsQuery = { group: ['nurse'] }

/** The ASP app's lab "status order" (labs.html nextStage): the button advances one step. OwlRx tracker
 *  labs carry `draw_status` (pending → scheduled → collected; redraw_needed is set through the re-draw
 *  dialog, never by the arrow), so the advance order is the OwlRx one. */
export const POT_LAB_DRAW_ORDER = ['pending', 'scheduled', 'collected'] as const
export function nextLabDrawStatus(current: string | null | undefined): string | null {
  const i = POT_LAB_DRAW_ORDER.indexOf((current ?? 'pending') as typeof POT_LAB_DRAW_ORDER[number])
  if (i < 0) return POT_LAB_DRAW_ORDER[0]
  return i < POT_LAB_DRAW_ORDER.length - 1 ? POT_LAB_DRAW_ORDER[i + 1] : null
}

/** Where "Start re-care POT" lands: the classic form, pre-seeded from the patient's latest assessment
 *  (the form reads these query keys). */
export function potRecareplanStartUrl(it: { id: string; patient_id: string; therapy_id: string | null }): string {
  const q = new URLSearchParams({ patientId: it.patient_id, recareplanId: it.id })
  if (it.therapy_id) q.set('therapyId', it.therapy_id)
  return `/pot/new?${q.toString()}`
}

// ── Agent B — enrollment / queue / patients / profile screens ────────────────────────────────
// Only what web/src/api/client.ts does not already export. Reads stay on the typed client.

/** PUT carerx/patients/{id}/enrollment/review — the ASP "Approve / Reject / Request Changes" row
 *  actions (spec §6/§7). The server appends to review_history and sets enrollment_status to
 *  approved | rejected | changes_requested. An approve that fails the gate is a 422
 *  `enrollment_incomplete` whose `missing` names each item. */
export type PotReviewAction = 'approve' | 'reject' | 'request_changes'
export const reviewPotEnrollment = (patientId: string, action: PotReviewAction, notes?: string) =>
  req<{ patient_id: string; enrollment: Record<string, unknown> }>('PUT', `/carerx/patients/${patientId}/enrollment/review`, { action, notes })

/** One row of GET carerx/visits (CareRxBillingFunctions.ListCareRxVisits) — the typed client
 *  returns `any[]`; this is the shape the profile's "Plans of Treatment" card reads. */
export interface PotVisitRow {
  id: string; patient_id: string; agency_id: string | null
  visit_date: string | null; visit_type: string; duration_hrs: number; mileage: number
  after_hours: boolean; weekend: boolean; holiday: boolean; billed: boolean
  therapy_id: string | null; therapy_drug: string | null; patient_name: string | null
  city?: string | null; state_code?: string | null; zip?: string | null
  // owner 2026-10-29: carried by the list itself (it was read from the dashboard's newest 500 visits)
  nurse_id?: string | null; nurse_name?: string; patient_initials?: string; mrn?: string | null
}

/** dbo.carerx_enrollment as GET carerx/patients/{id}/enrollment returns it (SELECT *). */
export interface PotEnrollmentRow {
  patient_id: string
  enrollment_status: string
  assigned_nurse_id: string | null
  assigned_at: string | null
  enrolled_at: string | null
  enrolled_by: string | null
  episode: string | null
  review_status: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  approved_at?: string | null
  review_history?: string | null
  intake_notes: string | null
  dose_sig?: string | null
  city?: string | null
  zip?: string | null
  height_inches_at_enrollment?: number | null
  weight_ounces_at_enrollment?: number | null
  updated_at: string
}
export interface PotReviewHistoryEntry { action: string; by: string | null; at: string; notes: string }
export function parseReviewHistory(raw: string | null | undefined): PotReviewHistoryEntry[] {
  if (!raw) return []
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return []
    return arr.filter((e): e is PotReviewHistoryEntry => typeof e === 'object' && e !== null && 'action' in e)
  } catch { return [] }
}

// ── Status vocabulary: OwlRx enrollment_status → the ASP badge (spec §6 table) ───────────────
// asp-pot: draft / pending_review / approved / awaiting_signature / signed / signature_declined /
// inactive / awaiting_assignment / assigned / pharmacist_evaluated / active / rejected.
// OwlRx:   new / pending_review / approved / rejected / changes_requested / awaiting_assignment /
//          assigned / in_review / active / completed / enrolled / assessment_only.
// Signature states live on the DocuSign envelope, not on enrollment_status, so they are not here.
export interface PotStatusBadge { label: string; bg: string; fg: string }
export const POT_STATUS: Record<string, PotStatusBadge> = {
  new:                 { label: 'Draft',                bg: '#E8E8E8', fg: '#605E5C' },
  changes_requested:   { label: 'Changes Requested',    bg: '#E8E8E8', fg: '#605E5C' },
  pending_review:      { label: 'Pending Review',       bg: '#FFF4CE', fg: '#986A00' },
  approved:            { label: 'Approved',             bg: '#E0F0E0', fg: '#107C10' },
  awaiting_assignment: { label: 'Approved',             bg: '#FFE7D0', fg: '#D4943A' },
  assigned:            { label: 'Assigned',             bg: '#EBF5FF', fg: '#3B82F6' },
  in_review:           { label: 'Pharmacist Evaluated', bg: '#F0E7FF', fg: '#6B3FA0' },
  active:              { label: 'Active',               bg: '#DFF6DD', fg: '#107C10' },
  enrolled:            { label: 'Active',               bg: '#DFF6DD', fg: '#107C10' },
  rejected:            { label: 'Rejected',             bg: '#FEF0F0', fg: '#A4262C' },
  completed:           { label: 'Inactive',             bg: '#F0F0F0', fg: '#8A8886' },
  assessment_only:     { label: 'Inactive',             bg: '#F0F0F0', fg: '#8A8886' },
}
export const potStatus = (s: string | null | undefined): PotStatusBadge =>
  POT_STATUS[s ?? ''] ?? { label: (s ?? '—').replace(/_/g, ' '), bg: '#E8E8E8', fg: '#605E5C' }

/** The ASP filter values (spec §6 / §7 selects) → the OwlRx statuses each one covers. */
export const POT_FILTER_STATUSES: Record<string, string[]> = {
  draft: ['new', 'changes_requested'],
  pending_review: ['pending_review'],
  approved: ['approved', 'awaiting_assignment'],
  assigned: ['assigned'],
  pharmacist_evaluated: ['in_review'],
  active: ['active', 'enrolled'],
  rejected: ['rejected'],
  inactive: ['completed', 'assessment_only', 'rejected'],
}

// ── Height / weight conversions for the ASP fields (Height `5'10"`, Weight kg / lb) ──────────
export function parseHeightInches(text: string): number | undefined {
  const t = text.trim(); if (!t) return undefined
  const ftIn = t.match(/^(\d+)\s*(?:'|ft)\s*(\d+(?:\.\d+)?)?\s*(?:"|in)?$/i)
  if (ftIn) return Number(ftIn[1]) * 12 + Number(ftIn[2] ?? 0)
  const cm = t.match(/^(\d+(?:\.\d+)?)\s*cm$/i)
  if (cm) return Math.round(Number(cm[1]) / 2.54 * 100) / 100
  const n = Number(t.replace(/in$/i, '').trim())
  return Number.isFinite(n) && n > 0 ? n : undefined
}
export function formatHeight(inches: number | null | undefined): string {
  if (inches == null || !Number.isFinite(inches) || inches <= 0) return ''
  const ft = Math.floor(inches / 12); const rem = Math.round((inches - ft * 12) * 10) / 10
  return `${ft}'${rem}"`
}
export const lbToOunces = (lb: number) => Math.round(lb * 16 * 100) / 100
export const kgToLb = (kg: number) => Math.round(kg * 2.20462 * 100) / 100
export const ouncesToLb = (oz: number) => Math.round(oz / 16 * 100) / 100

// ── Intake-notes round trip: every ASP field without an OwlRx column is kept as one line ─────
// "Section NN — Label: value". Parsing is tolerant: lines that do not match stay as free notes.
export const POT_NOTE_PREFIX = /^Section (\d{2}) — ([^:]+): ?(.*)$/
export function serializeNoteFields(fields: { section: string; label: string; value: string }[], free: string): string {
  const lines = fields.filter(f => f.value.trim() !== '').map(f => `Section ${f.section} — ${f.label}: ${f.value.trim()}`)
  const tail = free.trim()
  return [...lines, ...(tail ? ['', tail] : [])].join('\n')
}
export function parseNoteFields(notes: string | null | undefined): { fields: Map<string, string>; free: string } {
  const fields = new Map<string, string>(); const free: string[] = []
  for (const line of (notes ?? '').split(/\r?\n/)) {
    const m = line.match(POT_NOTE_PREFIX)
    if (m) fields.set(`${m[1]}|${m[2].trim()}`, m[3]); else free.push(line)
  }
  return { fields, free: free.join('\n').trim() }
}

// ── Agent H — Admin portal (/pot/admin), DSR, Feedback queue, Roadmap and the global overlays ───
// Only what web/src/api/client.ts does not already export. Every admin tab names the OwlRx
// capability it rides on; where OwlRx has none the tab says so (GAP(D-POT-2) comments in the tab).

/** Tenant-scoped account state changes (ManageUsersPage contract): POST /users/{id}/deactivate |
 *  /reactivate. The ASP "Disable / Enable" row buttons. */
export const potDeactivateUser = (accountId: string) =>
  req<{ ok?: boolean }>('POST', `/users/${accountId}/deactivate`)
export const potReactivateUser = (accountId: string) =>
  req<{ ok?: boolean }>('POST', `/users/${accountId}/reactivate`)

/** The ASP admin tab keys, in admin.html order. */
export type PotAdminTab =
  | 'users' | 'teams' | 'performance' | 'consent' | 'training' | 'audit' | 'security' | 'aiaudit'
  | 'template' | 'privacy' | 'roles' | 'feedback' | 'roadmap' | 'settings' | 'apikeys' | 'platformkeys' | 'docusign'
export const POT_ADMIN_TABS: { key: PotAdminTab; label: string }[] = [
  { key: 'users', label: 'User Management' },
  { key: 'teams', label: 'Teams' },
  { key: 'performance', label: 'Nurse Performance' },
  { key: 'consent', label: 'AI Consent Log' },
  { key: 'training', label: 'Training Log' },
  { key: 'audit', label: 'Audit Log' },
  { key: 'security', label: 'Security Log' },
  { key: 'aiaudit', label: 'AI Audit' },
  { key: 'template', label: 'PDF Template' },
  { key: 'privacy', label: 'Privacy Notice' },
  { key: 'roles', label: 'Roles & Permissions' },
  { key: 'feedback', label: 'Feedback Triage' },
  { key: 'roadmap', label: 'Roadmap' },
  { key: 'settings', label: 'Settings' },
  { key: 'apikeys', label: 'API Keys' },
  { key: 'platformkeys', label: 'Platform Keys' },
  { key: 'docusign', label: 'DocuSign' },
]
export const isPotAdminTab = (s: string | null | undefined): s is PotAdminTab =>
  POT_ADMIN_TABS.some(t => t.key === s)

/** Browser-local persistence for the overlays (privacy ribbon, announcement "seen" cache). Every
 *  read/write is wrapped: a private window or blocked storage must never break the page. */
export function readLocal<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}
export function writeLocal(key: string, value: unknown): void {
  try { window.localStorage.setItem(key, JSON.stringify(value)) } catch { /* storage unavailable — the overlay simply shows again next visit */ }
}

/** The ASP feedback widget's four chips → OwlRx feedback categories (client.ts FeedbackInput). */
export type PotFeedbackKind = 'bug' | 'feature' | 'question' | 'praise'
export const POT_FEEDBACK_KINDS: { kind: PotFeedbackKind; label: string; category: 'bug' | 'feature_request' | 'other' }[] = [
  { kind: 'bug', label: '🐛 Bug', category: 'bug' },
  { kind: 'feature', label: '💡 Feature', category: 'feature_request' },
  { kind: 'question', label: '❓ Question', category: 'other' },
  { kind: 'praise', label: '⭐ Praise', category: 'other' },
]

/** Announcements ride on OwlRx in-app notifications: a notification whose event_type names an
 *  announcement is shown as the ASP "What's new" pop-up and acknowledged with mark-read. */
export const isAnnouncementEvent = (eventType: string | null | undefined): boolean =>
  /announce|whats_new|what_s_new|release_note/i.test(eventType ?? '')
/** Default lock on the "I've read this" button (announcements.js ackDelaySec default). */
export const POT_ANNOUNCEMENT_ACK_DELAY_SEC = 10

/** Duration helpers for the Nurse Performance tab (ASP timerDuration is seconds). */
export const secondsToMinutes = (s: number | null | undefined): number => (s && s > 0 ? Math.round(s / 60) : 0)

/** Minimal CSV writer for the admin "Export CSV" buttons (client-side, current rows only). */
export function toCsv(header: string[], rows: (string | number | null | undefined)[][]): string {
  const cell = (v: string | number | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`
  return [header.map(cell).join(','), ...rows.map(r => r.map(cell).join(','))].join('\n')
}
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

/** Security-relevant audit actions (the ASP Security Log event types) picked out of the OwlRx audit
 *  catalog by name; the Audit Log tab takes the clinical/form entity types the same way. */
export const SECURITY_ACTION_RE = /login|logout|auth|mfa|lock|password|session|invite|user\.|account|role|permission|api_key|apikey|sso|csrf|carerx\.ai\.(consent_|audit_exported)|carerx\.training\./i
export const FORM_ENTITY_RE = /pot|assessment|visit|enrollment|care_plan|careplan|pcw|consent|document|esign/i

/** Markdown-lite used by announcements.js (## heading, - bullet, **bold**, blank line = paragraph). */
export type AnnouncementBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'bullet'; icon: string; text: string }
  | { kind: 'para'; lines: string[] }
const EMOJI_RE = /^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s*/u
export function parseAnnouncementBody(body: string | null | undefined): AnnouncementBlock[] {
  const out: AnnouncementBlock[] = []
  let para: string[] = []
  const flush = () => { if (para.length) { out.push({ kind: 'para', lines: para }); para = [] } }
  for (const raw of String(body ?? '').replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim()
    if (!line) { flush(); continue }
    const h = line.match(/^##\s+(.+)/)
    if (h) { flush(); out.push({ kind: 'heading', text: h[1] }); continue }
    const li = line.match(/^[-*]\s+(.+)/)
    if (li) {
      flush()
      const m = li[1].match(EMOJI_RE)
      out.push({ kind: 'bullet', icon: m ? m[1] : '✅', text: m ? li[1].slice(m[0].length) : li[1] })
      continue
    }
    para.push(line)
  }
  flush()
  return out
}
/** `**bold**` → segments; rendered as <strong> by the overlay (no innerHTML). */
export function splitBold(text: string): { bold: boolean; text: string }[] {
  const parts: { bold: boolean; text: string }[] = []
  const re = /\*\*(.+?)\*\*/g
  let last = 0
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0
    if (i > last) parts.push({ bold: false, text: text.slice(last, i) })
    parts.push({ bold: true, text: m[1] })
    last = i + m[0].length
  }
  if (last < text.length) parts.push({ bold: false, text: text.slice(last) })
  return parts
}

// ── Agent E (billing) — /pot/billing: Agencies & Billing (asp-pot billing.html) on OwlRx data ──
// Row shapes the CareRx billing endpoints actually return (CareRxBillingFunctions.cs), typed here
// because web/src/api/client.ts declares them `any[]`; the typed wrappers live in billing/billingApi.ts. Capabilities the old app had and OwlRx has no endpoint for are marked GAP(D-POT-2) at the
// call site; nothing here invents a route.
/** GET carerx/agencies row. The ASP agency also carried type (hha | nurse_independent), EIN, W-9 flag
 *  and a street address — OwlRx stores none of those (GAP(D-POT-2): no column, no endpoint). */
export interface PotAgency {
  id: string; name: string; city: string | null; state_code: string | null
  contact_name: string | null; contact_email: string | null; contact_phone: string | null
  status: string; created_at: string
  /** D-POT-6 (mig 453): the asp-pot agency fields the LOA / invoice print. */
  address_line?: string | null; zip_code?: string | null
  agency_type?: 'hha' | 'nurse_independent' | null; ein?: string | null; w9_on_file?: boolean
}

/** GET carerx/agencies/{id}/rate-cards row (+ agency_id stamped by the fan-out). `per_visit` is the
 *  JSON string { first2hrs, additionalPerHr, admission, restart } the ASP app wrote. */
export interface PotRateCard {
  id: string; agency_id: string; effective_from: string; effective_to: string | null; per_visit: string | null
  mileage_per_mile: number; after_hours_pct: number; weekend_pct: number; holiday_pct: number
  prn_rate: number | null; admin_fee: number; min_visit_fee: number | null; notes: string | null
  /** D-POT-6 (mig 453): asp-pot perHour — "Per-hour fallback rate" on Exhibit A. */
  per_hour?: number
  /** mig 455: archived cards are never picked for a new invoice */
  archived_at?: string | null
}
export interface PotRateCardPerVisit { first2hrs: number; additionalPerHr: number; admission: number; restart: number }
export function parsePotPerVisit(raw: string | null | undefined): PotRateCardPerVisit {
  const out: PotRateCardPerVisit = { first2hrs: 0, additionalPerHr: 0, admission: 0, restart: 0 }
  if (!raw) return out
  try {
    const o = JSON.parse(raw) as Record<string, unknown>
    for (const k of Object.keys(out) as (keyof PotRateCardPerVisit)[]) { const v = Number(o[k]); if (Number.isFinite(v)) out[k] = v }
  } catch { /* a hand-edited per_visit blob renders as zeros rather than breaking the tab */ }
  return out
}

/** GET carerx/agencies/{id}/loas row (+ agency_id). The ASP LOA also exposed rate_card_id, created_at
 *  and the status select (draft → sent → signed → expired → voided); OwlRx lists neither rate_card_id
 *  nor created_at and has no status endpoint (GAP(D-POT-2)). */
export interface PotLoa {
  id: string; agency_id: string; loa_number: string; patient_initials: string | null; patient_mrn: string | null
  start_date: string | null; end_date: string | null; status: string; created_at?: string
}

/** GET carerx/agencies/{id}/invoices row (+ agency_id), with the ASP totalVisits as visit_count. */
export interface PotInvoice {
  id: string; agency_id: string; invoice_number: string; period_start: string; period_end: string
  total: number; status: string; created_at: string; approved_at: string | null
  /** visits billed on it (ASP totalVisits) */
  visit_count?: number
}

/** The ASP page listed everything across agencies; OwlRx lists per agency, so fan out. One agency
 *  failing (e.g. a 404 on a just-deactivated one) must not blank the whole tab. */
export async function fanOutPotAgencies<T>(agencies: PotAgency[], one: (agencyId: string) => Promise<T[]>): Promise<T[]> {
  const settled = await Promise.allSettled(agencies.map(a => one(a.id)))
  const out: T[] = []
  for (const s of settled) if (s.status === 'fulfilled') out.push(...s.value)
  return out
}

// ── Invoice lifecycle (PUT carerx/invoices/{id}/review) ──────────────────────────────────────
// ASP: draft / sent / partial / paid / voided, any-to-any via a select. OwlRx (§9.5): draft → submitted
// → pending_approval → approved → paid, plus reject (reason required) and resubmit. Void (mig 455) is the
// ASP void: a reason, and the visits are released to be invoiced again — from any state but paid.
export type PotInvoiceAction = 'submit' | 'start_review' | 'approve' | 'reject' | 'mark_paid' | 'void'
export interface PotInvoiceTransition { action: PotInvoiceAction; to: string; label: string }
export const POT_INVOICE_NEXT: Record<string, PotInvoiceTransition[]> = {
  draft:            [{ action: 'submit', to: 'submitted', label: 'Submitted' }],
  submitted:        [{ action: 'start_review', to: 'pending_approval', label: 'Pending approval' }, { action: 'reject', to: 'rejected', label: 'Rejected' }],
  pending_approval: [{ action: 'approve', to: 'approved', label: 'Approved' }, { action: 'reject', to: 'rejected', label: 'Rejected' }],
  approved:         [{ action: 'mark_paid', to: 'paid', label: 'Paid' }],
  rejected:         [{ action: 'submit', to: 'submitted', label: 'Submitted (resubmit)' }],
  paid:             [],
  voided:           [],
}
export const POT_INVOICE_VOID: PotInvoiceTransition = { action: 'void', to: 'voided', label: 'Voided' }
export const canVoidPotInvoice = (status: string) => status !== 'paid' && status !== 'voided'
export const POT_INVOICE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', pending_approval: 'Pending approval', approved: 'Approved', rejected: 'Rejected', paid: 'Paid', voided: 'Voided',
}

// ── LOA coverage gate (mirrors GenerateCareRxInvoice) ────────────────────────────────────────
/** The rate card the server will pick: latest effective_from on/before period_end. */
export function effectivePotRateCard(cards: PotRateCard[], periodEnd: string): PotRateCard | null {
  const eligible = cards.filter(c => c.effective_from <= periodEnd).sort((a, b) => b.effective_from.localeCompare(a.effective_from))
  return eligible[0] ?? null
}
/** D-CRX-31 S6: a rate card is inactive until an LOA that references it is signed. The OwlRx LOA list
 *  does not expose rate_card_id, so the gate can only be judged per agency: any signed LOA for the
 *  agency counts as coverage here; the server still checks the exact card. */
export type PotLoaCoverage = 'signed' | 'unsigned' | 'none'
export function potLoaCoverage(loas: PotLoa[]): PotLoaCoverage {
  if (loas.length === 0) return 'none'
  return loas.some(l => l.status === 'signed') ? 'signed' : 'unsigned'
}

/** Server error codes → the sentence the biller reads (ApiError.body / potApi req error.body). */
export function potBillingError(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const err = e as { status?: number; body?: { error?: string; message?: string } | null; message?: string } | null
  const code = err?.body?.error
  switch (code) {
    case 'no_unbilled_visits': return 'No uninvoiced visits found for this agency in the selected period'
    case 'no_rate_card': return 'No active rate card found for this agency covering the selected period'
    case 'rate_card_loa_unsigned': return 'This rate card has no signed LOA — a rate card is inactive until its LOA is signed'
    case 'self_approval_blocked': return 'This invoice bills your own visits — it must be approved by a backup approver'
    case 'separation_of_duties': return 'The approver cannot be the nurse on a visit billed by this invoice — route it to a backup approver'
    case 'illegal_transition': return err?.body?.message ? `Cannot change status: ${err.body.message}` : 'That status change is not allowed from the current status'
    case 'reason_required': return 'A rejection reason is required'
    case 'agency_not_found': return 'Agency not found'
    case 'name_required': return 'Agency name is required'
    case 'effective_from_required': return 'Agency and effective date are required'
    case 'period_required': return 'Agency and date range are required'
    default: return err?.body?.message || err?.message || fallback
  }
}

// ── Visit Billing Details (billing-modal.js) ─────────────────────────────────────────────────
/** What the pre-submit pop-up hands back (window._visitBilling in the ASP app). */
export interface PotVisitBillingDetails {
  address: string; city: string; state: string; zip: string
  mileageMi: number; visitType: 'infusion' | 'admission' | 'restart'; afterHours: boolean; holiday: boolean
}
/** One hit of the ASP ZIP/city type-ahead (GET /api/billing/cities?q=). */
export interface PotCityHit { city: string; state: string; zip?: string | null }
export type PotCityLookup = (q: string) => Promise<PotCityHit[]>
// GAP(D-POT-2): OwlRx has no city / ZIP lookup endpoint (asp-pot lib/geo-cities.js, GET
// /api/billing/cities?q=<zip|city>, GeoNames + Census). The modal takes a `lookupCities` prop and
// degrades to free text when none is supplied; wire one here when the backend adds it.
export const POT_CITY_LOOKUP: PotCityLookup | null = null

// ── Agent G — Physicians / Teams / Analytics / Insights / Drug Formulary ────────────────────
// Pure helpers + one write wrapper. These screens are reproductions of asp-pot pages that were
// computed server-side from JSON files (lib/analytics-routes.js, /api/patients/insights); OwlRx has
// no such endpoints, so the roll-ups are derived in the browser from lists the viewer can already
// read (classic dashboard, e-sign awaiting list, enrollment queue, lab worklist, PCW queue).

/** PioneerRx prescriber `status` → the ASP Active / Inactive badge. Null / empty = active. */
export function isPrescriberActive(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase()
  if (!s) return true
  return !(s.includes('inactive') || s.includes('delet') || s.includes('terminat') || s.includes('retire') || s === '0' || s === 'false' || s === 'n')
}

// ── Analytics (asp-pot GET /api/analytics/dashboard?range=) ──────────────────────────────────
export type PotAnalyticsRange = '7d' | '30d' | '90d' | '365d' | 'all'
export type PotAnalyticsScope = 'org' | 'team' | 'self'
export const POT_ANALYTICS_RANGES: { key: PotAnalyticsRange; label: string; sub: string; days: number | null }[] = [
  { key: '7d', label: 'Last 7 days', sub: 'last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', sub: 'last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', sub: 'last 90 days', days: 90 },
  { key: '365d', label: 'Last year', sub: 'last year', days: 365 },
  { key: 'all', label: 'All time', sub: 'all time', days: null },
]
export const POT_SCOPE_LABEL: Record<PotAnalyticsScope, string> = { org: 'Organization-wide', team: 'Your team', self: 'Your work only' }
export interface PotTopItem { name: string; count: number }
export interface PotTopNurse extends PotTopItem { avgDurationMin: number | null }
export interface PotAnalytics {
  scope: PotAnalyticsScope
  range: PotAnalyticsRange
  bucketMode: 'day' | 'week' | 'month'
  kpis: { totalInRange: number; awaiting: number; signedInRange: number; activeDrafts: number }
  lifecycle: { avgDraftToCompleteHrs: number | null; avgCompleteToSentHrs: number | null; avgSentToSignedHrs: number | null; pctSignedWithin30d: number | null }
  funnel: { completed: number; sent: number; signed: number; declined: number; voided: number }
  volume: { period: string; label: string; count: number }[]
  topNurses: PotTopNurse[] | null
  topDrugs: PotTopItem[]
  topDiagnoses: PotTopItem[]
  compliance: { staleAwaiting: number | null; overdueUnsigned: number }
}

const toMs = (iso: string | null | undefined): number | null => { const t = iso ? Date.parse(iso) : NaN; return Number.isFinite(t) ? t : null }

/** A range's length in days; null = all time. (`find(...)?.days ?? 30` turned "All time" into 30 days.) */
export function potRangeDays(range: PotAnalyticsRange): number | null {
  const r = POT_ANALYTICS_RANGES.find(x => x.key === range)
  return r ? r.days : 30
}
/** The legacy chart's bucket for a range: months for a year or more, weeks from 90 days, else days. */
export function potAnalyticsBucket(range: PotAnalyticsRange): PotAnalytics['bucketMode'] {
  const days = potRangeDays(range)
  return days === null || days >= 365 ? 'month' : days >= 90 ? 'week' : 'day'
}
/** The stats request Analytics makes for a range (the server counts every visit; owner 2026-10-29). */
export function potAnalyticsQuery(range: PotAnalyticsRange, now = Date.now()): PotStatsQuery {
  const days = potRangeDays(range)
  return { from: potSinceDays(days, now), group: ['nurse', 'drug', 'diagnosis'], bucket: potAnalyticsBucket(range) }
}
/** analytics-routes.js's numbers, read off GET carerx/pot/stats (counted server-side over every visit in scope).
 *  Voided POTs stay out of every aggregate except the funnel's "Voided" bar (as in the original). */
export function computePotAnalytics(st: PotStats, range: PotAnalyticsRange, scope: PotAnalyticsScope): PotAnalytics {
  const t = st.totals
  const bucketMode = potAnalyticsBucket(range)
  const volume = (st.volume ?? []).map(v => {
    const [y, m, d] = v.period.split('-')
    return bucketMode === 'month' ? { period: `${y}-${m}`, label: m, count: v.count } : { period: v.period, label: `${m}-${d}`, count: v.count }
  })
  const top = (rows: PotStatsGroupRow[] | undefined, n: number): PotTopItem[] => (rows ?? [])
    .filter(r => r.completed_in_range > 0).sort((a, b) => b.completed_in_range - a.completed_in_range).slice(0, n)
    .map(r => ({ name: r.key, count: r.completed_in_range }))
  const topNurses: PotTopNurse[] | null = scope === 'self' ? null : (st.groups.nurse ?? [])
    .filter(r => r.completed_in_range > 0).sort((a, b) => b.completed_in_range - a.completed_in_range).slice(0, 10)
    .map(r => ({ name: r.name || r.key || '(unknown)', count: r.completed_in_range, avgDurationMin: r.worked_avg_min === null ? null : Math.round(r.worked_avg_min) }))
  return {
    scope, range, bucketMode,
    kpis: { totalInRange: t.completed, awaiting: t.awaiting, signedInRange: t.signed, activeDrafts: t.active_drafts },
    lifecycle: {
      avgDraftToCompleteHrs: t.avg_draft_to_complete_hrs, avgCompleteToSentHrs: t.avg_complete_to_sent_hrs,
      avgSentToSignedHrs: null, // GAP(D-POT-2): signed_at per envelope is not in the stats yet.
      pctSignedWithin30d: null, // GAP(D-POT-2): needs sent_at + signed_at per envelope (see above).
    },
    funnel: { completed: t.completed, sent: t.sent, signed: t.signed, declined: t.declined, voided: t.voided },
    volume, topNurses, topDrugs: top(st.groups.drug, 5), topDiagnoses: top(st.groups.diagnosis, 5),
    compliance: { staleAwaiting: t.stale_awaiting_7d, overdueUnsigned: t.overdue_unsigned_30d },
  }
}

/** analytics.html fmtHours: minutes under an hour, hours under two days, days after. Null = "No data". */
export function formatPotHours(h: number | null): string | null {
  if (h === null) return null
  if (h < 1) return `${Math.round(h * 60)} min`
  if (h < 48) return `${h.toFixed(1)} hrs`
  return `${Math.round(h / 24)} days`
}

// ── Insights care score (asp-pot server.js calculateCareScore) ──────────────────────────────
export type PotCareGrade = 'on_track' | 'needs_attention' | 'at_risk'
export interface PotCareScoreComponent { name: string; weight: number; score: number | null; detail: string }
export interface PotCareScore { total: number; grade: PotCareGrade; components: PotCareScoreComponent[] }
export interface PotCareScoreInput {
  enrolledAt: string | null
  /** completed POTs for the patient */
  assessments: number
  /** POT visits of any status */
  visits: number
  /** required-for-start labs on the worklist; 0 = nothing required (scores 100, as in the original) */
  requiredLabs: number
  /** of those, the ones that are not missing / expired / re-draw */
  labsPresent: number
  /** PCW (clinical review) status; null = not started */
  pcwStatus: string | null
}
/** Same six components and weights as the ASP app. Two of them have no OwlRx source (Counseling, and
 *  the PCW "n/8 sections" detail); a component with `score: null` is left out and the remaining
 *  weights are re-normalised so the 0–100 scale and the 90 / 70 grade cut-offs keep their meaning. */
export function potCareScore(i: PotCareScoreInput, now = Date.now()): PotCareScore {
  const enrolledMs = toMs(i.enrolledAt)
  const enrolledDays = enrolledMs === null ? 0 : Math.max(0, Math.floor((now - enrolledMs) / 86_400_000))
  const expectedVisits = Math.max(1, Math.floor(enrolledDays / 56))
  const pcw = (i.pcwStatus ?? '').toLowerCase()
  const pcwRate = !pcw ? 0 : (pcw === 'complete' || pcw === 'completed' || pcw === 'locked' || pcw === 'approved' || pcw === 'cleared') ? 100 : 50
  const weeks = enrolledDays / 7
  const components: PotCareScoreComponent[] = [
    { name: 'Assessment Completion', weight: 20, score: i.assessments > 0 ? 100 : 0, detail: `${i.assessments} completed` },
    { name: 'Visit Adherence', weight: 20, score: Math.min(100, Math.round((i.visits / expectedVisits) * 100)), detail: `${i.visits}/${expectedVisits} expected` },
    { name: 'Lab Compliance', weight: 20, score: i.requiredLabs ? Math.round((i.labsPresent / i.requiredLabs) * 100) : 100, detail: `${i.labsPresent}/${i.requiredLabs} required labs` },
    { name: 'Clinical Review', weight: 15, score: pcwRate, detail: i.pcwStatus ? i.pcwStatus.replace(/_/g, ' ') : 'Not started' }, // GAP(D-POT-2): no per-section n/8 progress in OwlRx
    { name: 'Counseling', weight: 10, score: null, detail: 'Not tracked' }, // GAP(D-POT-2): PCW §8 counselMethod has no OwlRx field
    { name: 'Therapy Progress', weight: 15, score: Math.min(100, Math.round((weeks / 16) * 100)), detail: `${Math.round(weeks)}/16 weeks` },
  ]
  let weightSum = 0; let weighted = 0
  for (const c of components) { if (c.score === null) continue; weightSum += c.weight; weighted += c.score * c.weight }
  const total = weightSum ? Math.round(weighted / weightSum) : 0
  const grade: PotCareGrade = total >= 90 ? 'on_track' : total >= 70 ? 'needs_attention' : 'at_risk'
  return { total, grade, components }
}

/** insights.html status filter values → the OwlRx enrollment statuses each one covers. `pcw_hold`
 *  is resolved by the page from the screening-hold worklist instead (no enrollment status for it). */
export const POT_INSIGHTS_STATUS_FILTERS: { value: string; label: string; statuses: string[] }[] = [
  { value: 'all', label: 'All Statuses', statuses: [] },
  { value: 'active', label: 'Active', statuses: ['active', 'enrolled'] },
  { value: 'assigned', label: 'Assigned', statuses: ['assigned'] },
  { value: 'pharmacist_cleared', label: 'Cleared', statuses: ['approved'] },
  { value: 'pharmacist_evaluated', label: 'Evaluated', statuses: ['in_review'] },
  { value: 'awaiting_assignment', label: 'Awaiting Assignment', statuses: ['awaiting_assignment'] },
  { value: 'pcw_hold', label: 'On Hold', statuses: [] },
]

// ── P0: POT ownership transfer + co-visit share (CareRxPotFunctions.Reassign / Share / ShareRevoke) ──
// Both endpoints are pharmacy_staff / admin only (a nurse gets 403). `nurse_id` / `account_id` is the
// nurse's LOGIN (account) id — NurseRow.nurse_id, the same key the classic dashboard rows carry in
// `nurse_id`. Every grant / transfer lands as an audited carerx_visit_assignments history row.
/** PUT carerx/visits/{id}/reassign — moves an OPEN visit (status scheduled | claimed) to another nurse.
 *  `reason` is required (422 `reason_required`); 422 `nurse_not_found_or_inactive` when the account
 *  is not an active login of this pharmacy; 404 `visit_not_found_or_not_open` once the visit is closed. */
export const reassignCareRxPot = (visitId: string, body: { nurse_id: string; reason: string }) =>
  req<{ id: string; nurse_id: string; nurse_name: string; previous_nurse_id: string | null }>('PUT', `/carerx/visits/${visitId}/reassign`, body)
/** POST carerx/visits/{id}/share — grant a second account read access to the POT (co-visit /
 *  supervision). Idempotent: an active grant for the same account is not duplicated. */
export const shareCareRxPot = (visitId: string, body: { account_id: string; reason?: string }) =>
  req<{ visit_id: string; shared_with: string }>('POST', `/carerx/visits/${visitId}/share`, body)
/** DELETE carerx/visits/{id}/share/{accountId} — revoke a share; `revoked` is false when none was active. */
export const unshareCareRxPot = (visitId: string, accountId: string) =>
  req<{ visit_id: string; revoked: boolean }>('DELETE', `/carerx/visits/${visitId}/share/${accountId}`)

// ── Teams: reassign a POT visit (asp-pot POST /api/assessments/:id/reassign) ─────────────────
/** Teams-page alias of reassignCareRxPot: the visit moves through PUT carerx/visits/{id}/reassign
 *  (NOT the staffing `staff` action, which is pharmacy-staffing only and 403s a nurse manager's
 *  session). `nurseLoginId` is NurseRow.nurse_id; the reason is required by the endpoint. */
export const reassignPotVisit = (visitId: string, nurseLoginId: string, reason: string) =>
  reassignCareRxPot(visitId, { nurse_id: nurseLoginId, reason })

/** manager.html "Duration": whole minutes from the worked timer, "—" when none. */
export function formatPotDuration(seconds: number | null | undefined): string {
  return seconds && seconds > 0 ? `${Math.floor(seconds / 60)}min` : '—'
}

// ── Agent F (pcw) — PCW Intake / PCW Pharmacist ("Clinical Review (PCW)") ───────────────────
// The ASP app's pcw.html rides ONE record per patient: { status, phase, sections: { sec1..sec8:
// { status, data } }, aiCallCount }. OwlRx already has the same two-stage record (dbo.carerx_pcw,
// migration 129: phase intake → pharmacist; CareRxPcwStageFunctions.cs owns the section PUTs and
// complete-intake; CareRxPcwFunctions.cs owns create / read / determination / benefits / dispense
// columns). The section JSON column is the asp-pot shape, so every ASP field is persisted under the
// SAME data keys the old app used (sec1.triage, sec2.priorTherapy, sec4.subdomains …) and the
// handful of columns OwlRx has (pa_status, pa_ref, pa_expiry, copay_assist, dispense_lot,
// counsel_method, therapy_determination) are mirrored on save.

export type PcwSectionStatus = 'pending' | 'in_review' | 'complete' | 'hold'
export type PcwSectionNum = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
export const PCW_SECTION_NUMS: PcwSectionNum[] = [1, 2, 3, 4, 5, 6, 7, 8]
export const PCW_INTAKE_SECTIONS: PcwSectionNum[] = [1, 2, 3, 5, 6]
export const PCW_PHARM_SECTIONS: PcwSectionNum[] = [4, 7, 8]
/** Rail labels (short) and section titles (long), verbatim from pcw.html. */
export const PCW_RAIL_LABELS: Record<PcwSectionNum, string> = {
  1: 'Intake Verification', 2: 'Clinical Profile', 3: 'Med Reconciliation', 4: 'Clinical Assessment',
  5: 'Benefits & Access', 6: 'REMS & Risk', 7: 'Care Plan', 8: 'Order & Dispense',
}
export const PCW_SECTION_TITLES: Record<PcwSectionNum, string> = {
  1: 'Intake Verification', 2: 'Clinical Profile Review', 3: 'Medication Reconciliation & Allergy Review',
  4: 'Pharmacist Clinical Assessment', 5: 'Benefits & Access', 6: 'REMS & Risk Programs',
  7: 'Pharmaceutical Care Plan', 8: 'Order Verification & Dispense',
}

// Section data shapes — the asp-pot keys. Unknown extra keys (the CareRx PCW workflow screen writes
// its own D-CRX-51 keys into the same JSON) are preserved by spreading the stored object on save.
export interface PcwPriorTherapy { drug: string; startDate: string; endDate: string; reasonDiscontinued: string; response: string }
export interface PcwMedication { drug: string; strength: string; route: string; frequency: string; indication: string }
export interface PcwOtc { name: string; dose: string; frequency: string }
export interface PcwAllergy { allergen: string; reaction: string; severity: string }
export interface PcwAdr { drug: string; reaction: string; date: string; action: string }
export interface PcwSec1 { triage: string; notes: string; startOfCare: string; dateOfLastService: string; dateOfNextService: string; nextServiceIntervalDays: string; nextServiceAnchor: 'manual' | 'last_service' | 'start_of_care' | '' }
export interface PcwSec2 { primaryDx: string; icd10: string; dxConfirm: string; dxNotes: string; comorbidities: string; severity: string; goals: string; priorTherapy: PcwPriorTherapy[] }
export interface PcwSec3 { nkda: boolean; medications: PcwMedication[]; otc: PcwOtc[]; allergies: PcwAllergy[]; adr: PcwAdr[] }
export interface PcwSec5 { paStatus: string; paRef: string; paExpiry: string; copayAssist: string; copayProgram: string; foundation: string; foundationName: string; formulary: string; ndc: string; notes: string }
export interface PcwSec6 { remsStatus: string; remsDate: string; pregnancy: string; notes: string }
export interface PcwVersionEntry { version: number; changeType: string; by: string; at: string; previousNarrative?: string }
export interface PcwSec7 { shortTermGoals: string; longTermMarkers: string; monitoring: string; education: string; narrative: string; version: number; versionHistory: PcwVersionEntry[]; lastUpdated: string }
export interface PcwSec8 {
  lot: string; exp: string; qty: string; daysSupply: string
  verifyDrug: boolean; verifyNDC: boolean; verifyLot: boolean
  couns_name: boolean; couns_route: boolean; couns_directions: boolean; couns_sideeffects: boolean; couns_storage: boolean
  couns_interactions: boolean; couns_refill: boolean; couns_missed: boolean; couns_emergency: boolean
  counselMethod: string; counselNotes: string; license: string; credentials: string
  signedAt?: string; signedBy?: string
}
export type PcwFindingAction = 'pending' | 'accepted' | 'overridden' | 'dismissed'
export interface PcwAiFinding { severity: string; description: string; recommendation: string; citation: string; pharmacistAction: PcwFindingAction; overrideRationale?: string; actionAt?: string }
export type PcwSubdomainKey = '4.1' | '4.2' | '4.3' | '4.4' | '4.5' | '4.6' | '4.7' | '4.8'
export const PCW_SUBDOMAINS: PcwSubdomainKey[] = ['4.1', '4.2', '4.3', '4.4', '4.5', '4.6', '4.7', '4.8']
export const PCW_SUBDOMAINS_AI: PcwSubdomainKey[] = ['4.1', '4.2', '4.3', '4.4', '4.5', '4.6', '4.7']
export interface PcwSubdomain {
  status: PcwSectionStatus; aiFindings?: PcwAiFinding[]; aiOutput?: string; inputHash?: string; aiRunAt?: string; notes?: string; completedAt?: string
  /** Owner 2026-10-29: completed on the pharmacist's notes because AI was unavailable (audited server-side). */
  completedWithoutAi?: { reason: string; at: string; by: string }
  // 4.8 only
  determination?: string; holdReason?: string; license?: string; credentials?: string; signedAt?: string; signedBy?: string
}
export interface PcwSec4 { subdomains: Partial<Record<PcwSubdomainKey, PcwSubdomain>>; aiCallCount: number }
export const PCW_SUBDOMAIN_META: Record<PcwSubdomainKey, { title: string; desc: string }> = {
  '4.1': { title: 'Indication Appropriateness', desc: 'Is this drug FDA-approved for this diagnosis?' },
  '4.2': { title: 'Pharmacokinetic Fit', desc: 'Does renal/hepatic function support standard dosing?' },
  '4.3': { title: 'Pharmacodynamic Considerations', desc: 'Expected response timeline and mechanism' },
  '4.4': { title: 'Drug-Drug Interactions', desc: 'Screen all medications for interactions' },
  '4.5': { title: 'Drug-Disease Interactions', desc: 'Comorbidity conflicts with prescribed drug' },
  '4.6': { title: 'Contraindications & Warnings', desc: 'Black box warnings and contraindications' },
  '4.7': { title: 'Risk Factor Assessment', desc: 'Composite risk: infection, malignancy, CV, pregnancy' },
  '4.8': { title: 'Therapy Determination', desc: 'Final pharmacist sign-off' },
}
export const PCW_AI_CALL_CAP = 10

/**
 * Why AI cannot run for this user right now — no AI consent / security training (403), the AI module off, the
 * AI provider down (5xx) or unreachable — or null for an ordinary error (a bad prompt, the call cap). Owner
 * 2026-10-29: then a §4 sub-domain may be completed on the pharmacist's own notes, so a pharmacy without AI
 * can still finish its clinical review (the POT now waits on it).
 */
export function pcwAiUnavailableReason(e: unknown): string | null {
  const status = (e as { status?: number } | null)?.status
  const body = (e as { body?: { error?: string; message?: string } } | null)?.body
  if (status === 403) return body?.message ?? body?.error ?? 'AI is not enabled for this user'
  if (status === 0 || status === undefined || status >= 500) return body?.message ?? 'The AI service is unavailable'
  return null
}

export interface PcwSection<T> { status: PcwSectionStatus; data: T; lastSavedAt?: string; lastSavedBy?: string; raw: Record<string, unknown> }
export interface PcwSections {
  sec1: PcwSection<PcwSec1>; sec2: PcwSection<PcwSec2>; sec3: PcwSection<PcwSec3>; sec4: PcwSection<PcwSec4>
  sec5: PcwSection<PcwSec5>; sec6: PcwSection<PcwSec6>; sec7: PcwSection<PcwSec7>; sec8: PcwSection<PcwSec8>
}
export type PcwSectionKey = keyof PcwSections
export const pcwKey = (n: PcwSectionNum): PcwSectionKey => `sec${n}` as PcwSectionKey

const pcwStr = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const pcwBool = (v: unknown): boolean => v === true || v === 'true' || v === 1
const pcwNum = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : d)
const pcwObj = (v: unknown): Record<string, unknown> => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {})
const pcwArr = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.map(pcwObj) : [])
const pcwStatus = (v: unknown): PcwSectionStatus => (v === 'in_review' || v === 'complete' || v === 'hold' ? v : 'pending')

export const EMPTY_SEC1: PcwSec1 = { triage: '', notes: '', startOfCare: '', dateOfLastService: '', dateOfNextService: '', nextServiceIntervalDays: '', nextServiceAnchor: '' }
export const EMPTY_SEC2: PcwSec2 = { primaryDx: '', icd10: '', dxConfirm: '', dxNotes: '', comorbidities: '', severity: '', goals: '', priorTherapy: [] }
export const EMPTY_SEC3: PcwSec3 = { nkda: false, medications: [], otc: [], allergies: [], adr: [] }
export const EMPTY_SEC4: PcwSec4 = { subdomains: {}, aiCallCount: 0 }
export const EMPTY_SEC5: PcwSec5 = { paStatus: '', paRef: '', paExpiry: '', copayAssist: '', copayProgram: '', foundation: '', foundationName: '', formulary: '', ndc: '', notes: '' }
export const EMPTY_SEC6: PcwSec6 = { remsStatus: '', remsDate: '', pregnancy: '', notes: '' }
export const EMPTY_SEC7: PcwSec7 = { shortTermGoals: '', longTermMarkers: '', monitoring: '', education: '', narrative: '', version: 1, versionHistory: [], lastUpdated: '' }
export const EMPTY_SEC8: PcwSec8 = {
  lot: '', exp: '', qty: '', daysSupply: '', verifyDrug: false, verifyNDC: false, verifyLot: false,
  couns_name: false, couns_route: false, couns_directions: false, couns_sideeffects: false, couns_storage: false,
  couns_interactions: false, couns_refill: false, couns_missed: false, couns_emergency: false,
  counselMethod: '', counselNotes: '', license: '', credentials: '',
}

function readFinding(f: Record<string, unknown>): PcwAiFinding {
  const a = f.pharmacistAction
  return {
    severity: pcwStr(f.severity) || 'info', description: pcwStr(f.description), recommendation: pcwStr(f.recommendation), citation: pcwStr(f.citation),
    pharmacistAction: a === 'accepted' || a === 'overridden' || a === 'dismissed' ? a : 'pending',
    overrideRationale: f.overrideRationale === undefined ? undefined : pcwStr(f.overrideRationale),
    actionAt: f.actionAt === undefined ? undefined : pcwStr(f.actionAt),
  }
}
function readSubdomain(s: Record<string, unknown>): PcwSubdomain {
  const out: PcwSubdomain = { status: pcwStatus(s.status) }
  if (Array.isArray(s.aiFindings)) out.aiFindings = pcwArr(s.aiFindings).map(readFinding)
  for (const k of ['aiOutput', 'inputHash', 'aiRunAt', 'notes', 'completedAt', 'determination', 'holdReason', 'license', 'credentials', 'signedAt', 'signedBy'] as const)
    if (s[k] !== undefined && s[k] !== null) out[k] = pcwStr(s[k])
  return out
}
function readSec4(data: Record<string, unknown>, legacySubdomains: unknown): PcwSec4 {
  // asp-pot created `sec4.subdomains` beside `data` and then saved them INSIDE data — honour both.
  const src = pcwObj(data.subdomains ?? legacySubdomains)
  const subdomains: Partial<Record<PcwSubdomainKey, PcwSubdomain>> = {}
  for (const k of PCW_SUBDOMAINS) if (src[k] !== undefined) subdomains[k] = readSubdomain(pcwObj(src[k]))
  return { subdomains, aiCallCount: pcwNum(data.aiCallCount) }
}

/** Parse the carerx_pcw.sections JSON (string or object) into the typed 8-section record. */
export function parsePcwSections(raw: unknown): PcwSections {
  let root: Record<string, unknown> = {}
  if (typeof raw === 'string' && raw.trim() !== '') { try { root = pcwObj(JSON.parse(raw)) } catch { root = {} } }
  else root = pcwObj(raw)
  const sec = (n: PcwSectionNum) => pcwObj(root[`sec${n}`])
  const base = <T>(n: PcwSectionNum, data: T): PcwSection<T> => {
    const s = sec(n)
    return { status: pcwStatus(s.status), data, lastSavedAt: s.lastSavedAt === undefined ? undefined : pcwStr(s.lastSavedAt), lastSavedBy: s.lastSavedBy === undefined ? undefined : pcwStr(s.lastSavedBy), raw: pcwObj(s.data) }
  }
  const d1 = pcwObj(sec(1).data), d2 = pcwObj(sec(2).data), d3 = pcwObj(sec(3).data), d4 = pcwObj(sec(4).data)
  const d5 = pcwObj(sec(5).data), d6 = pcwObj(sec(6).data), d7 = pcwObj(sec(7).data), d8 = pcwObj(sec(8).data)
  const anchor = d1.nextServiceAnchor
  return {
    sec1: base(1, { triage: pcwStr(d1.triage), notes: pcwStr(d1.notes), startOfCare: pcwStr(d1.startOfCare), dateOfLastService: pcwStr(d1.dateOfLastService), dateOfNextService: pcwStr(d1.dateOfNextService), nextServiceIntervalDays: pcwStr(d1.nextServiceIntervalDays), nextServiceAnchor: anchor === 'manual' || anchor === 'last_service' || anchor === 'start_of_care' ? anchor : '' }),
    sec2: base(2, { primaryDx: pcwStr(d2.primaryDx), icd10: pcwStr(d2.icd10), dxConfirm: pcwStr(d2.dxConfirm), dxNotes: pcwStr(d2.dxNotes), comorbidities: pcwStr(d2.comorbidities), severity: pcwStr(d2.severity), goals: pcwStr(d2.goals),
      priorTherapy: pcwArr(d2.priorTherapy).map(t => ({ drug: pcwStr(t.drug), startDate: pcwStr(t.startDate), endDate: pcwStr(t.endDate), reasonDiscontinued: pcwStr(t.reasonDiscontinued), response: pcwStr(t.response) })) }),
    sec3: base(3, { nkda: pcwBool(d3.nkda),
      medications: pcwArr(d3.medications).map(m => ({ drug: pcwStr(m.drug), strength: pcwStr(m.strength), route: pcwStr(m.route), frequency: pcwStr(m.frequency), indication: pcwStr(m.indication) })),
      otc: pcwArr(d3.otc).map(o => ({ name: pcwStr(o.name), dose: pcwStr(o.dose), frequency: pcwStr(o.frequency) })),
      allergies: pcwArr(d3.allergies).map(a => ({ allergen: pcwStr(a.allergen), reaction: pcwStr(a.reaction), severity: pcwStr(a.severity) })),
      adr: pcwArr(d3.adr).map(a => ({ drug: pcwStr(a.drug), reaction: pcwStr(a.reaction), date: pcwStr(a.date), action: pcwStr(a.action) })) }),
    sec4: base(4, readSec4(d4, sec(4).subdomains)),
    sec5: base(5, { paStatus: pcwStr(d5.paStatus), paRef: pcwStr(d5.paRef), paExpiry: pcwStr(d5.paExpiry), copayAssist: pcwStr(d5.copayAssist), copayProgram: pcwStr(d5.copayProgram), foundation: pcwStr(d5.foundation), foundationName: pcwStr(d5.foundationName), formulary: pcwStr(d5.formulary), ndc: pcwStr(d5.ndc), notes: pcwStr(d5.notes) }),
    sec6: base(6, { remsStatus: pcwStr(d6.remsStatus), remsDate: pcwStr(d6.remsDate), pregnancy: pcwStr(d6.pregnancy), notes: pcwStr(d6.notes) }),
    sec7: base(7, { shortTermGoals: pcwStr(d7.shortTermGoals), longTermMarkers: pcwStr(d7.longTermMarkers), monitoring: pcwStr(d7.monitoring), education: pcwStr(d7.education), narrative: pcwStr(d7.narrative), version: pcwNum(d7.version, 1),
      versionHistory: pcwArr(d7.versionHistory).map(v => ({ version: pcwNum(v.version, 1), changeType: pcwStr(v.changeType), by: pcwStr(v.by), at: pcwStr(v.at), previousNarrative: v.previousNarrative === undefined ? undefined : pcwStr(v.previousNarrative) })), lastUpdated: pcwStr(d7.lastUpdated) }),
    sec8: base(8, { ...EMPTY_SEC8,
      lot: pcwStr(d8.lot), exp: pcwStr(d8.exp), qty: pcwStr(d8.qty), daysSupply: pcwStr(d8.daysSupply),
      verifyDrug: pcwBool(d8.verifyDrug), verifyNDC: pcwBool(d8.verifyNDC), verifyLot: pcwBool(d8.verifyLot),
      couns_name: pcwBool(d8.couns_name), couns_route: pcwBool(d8.couns_route), couns_directions: pcwBool(d8.couns_directions), couns_sideeffects: pcwBool(d8.couns_sideeffects), couns_storage: pcwBool(d8.couns_storage),
      couns_interactions: pcwBool(d8.couns_interactions), couns_refill: pcwBool(d8.couns_refill), couns_missed: pcwBool(d8.couns_missed), couns_emergency: pcwBool(d8.couns_emergency),
      counselMethod: pcwStr(d8.counselMethod), counselNotes: pcwStr(d8.counselNotes), license: pcwStr(d8.license), credentials: pcwStr(d8.credentials),
      signedAt: d8.signedAt === undefined ? undefined : pcwStr(d8.signedAt), signedBy: d8.signedBy === undefined ? undefined : pcwStr(d8.signedBy) }),
  }
}

/** The dbo.carerx_pcw row as GET carerx/pcw/{id} returns it (SELECT * → dictionary). */
export interface PotPcwRow {
  id: string; patient_id: string; therapy_id: string | null; assessment_id: string | null
  status: string; phase: string
  sections: PcwSections
  therapy_determination: string | null; signed_at: string | null; dispensed_at: string | null
  pa_status: string | null; pa_ref: string | null; pa_expiry: string | null; copay_assist: string | null
  dispense_lot: string | null; counsel_method: string | null
  locked_at: string | null; intake_completed_at: string | null; created_at: string | null; updated_at: string | null
}
const pcwNstr = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v))
/** GET carerx/pcw/{id} (CareRxPcwFunctions.Get) typed; the client.ts wrapper returns `Record<string, any>`. */
export async function getPotPcw(id: string): Promise<PotPcwRow> {
  const r = await req<{ pcw: Record<string, unknown> }>('GET', `/carerx/pcw/${id}`)
  const d = r.pcw ?? {}
  return {
    id: pcwStr(d.id), patient_id: pcwStr(d.patient_id), therapy_id: pcwNstr(d.therapy_id), assessment_id: pcwNstr(d.assessment_id),
    status: pcwStr(d.status) || 'draft', phase: pcwStr(d.phase) || 'intake',
    sections: parsePcwSections(d.sections),
    therapy_determination: pcwNstr(d.therapy_determination), signed_at: pcwNstr(d.signed_at), dispensed_at: pcwNstr(d.dispensed_at),
    pa_status: pcwNstr(d.pa_status), pa_ref: pcwNstr(d.pa_ref), pa_expiry: pcwNstr(d.pa_expiry), copay_assist: pcwNstr(d.copay_assist),
    dispense_lot: pcwNstr(d.dispense_lot), counsel_method: pcwNstr(d.counsel_method),
    locked_at: pcwNstr(d.locked_at), intake_completed_at: pcwNstr(d.intake_completed_at), created_at: pcwNstr(d.created_at), updated_at: pcwNstr(d.updated_at),
  }
}
export const pcwIsSigned = (row: Pick<PotPcwRow, 'status' | 'phase'>): boolean =>
  row.status === 'signed' || row.status === 'completed' || row.phase === 'complete'
/** The ASP banner / queue chip text for an OwlRx (status, phase). */
export function pcwStatusLabel(statusValue: string, phase: string): string {
  if (statusValue === 'signed' || statusValue === 'completed' || phase === 'complete') return 'signed'
  if (statusValue === 'locked') return 'locked'
  if (phase === 'pharmacist') return 'ready_for_clinical_review'
  return 'in_progress'
}

// ── 4.8 determination vocabulary: ASP → OwlRx therapy_determination (approved | denied | conditional)
export const PCW_DETERMINATIONS: { value: string; label: string }[] = [
  { value: 'approved', label: 'Approved — proceed to care plan and dispensing' },
  { value: 'approved_monitoring', label: 'Approved with Monitoring — additional monitoring parameters required' },
  { value: 'hold', label: 'Hold — waiting on labs, attestation, or additional info' },
  { value: 'recommend_alternative', label: 'Recommend Alternative — contact prescriber' },
]
export function owlDetermination(asp: string): 'approved' | 'denied' | 'conditional' | null {
  if (asp === 'approved') return 'approved'
  if (asp === 'approved_monitoring') return 'conditional'
  if (asp === 'recommend_alternative') return 'denied'
  return null   // 'hold' has no OwlRx column value — it lives in sec4.subdomains['4.8'] only
}
export const pcwDeterminationApproved = (asp: string | undefined, owl: string | null): boolean =>
  asp === 'approved' || asp === 'approved_monitoring' || owl === 'approved' || owl === 'conditional'

// ── Drug matrix match (asp-pot: medication includes brand or generic name) ───────────────────
export function matchPcwDrug<T extends { brandName: string; genericName: string }>(medication: string | null | undefined, matrix: T[]): T | null {
  const m = (medication ?? '').toLowerCase().trim()
  if (!m) return null
  return matrix.find(d => (d.brandName !== '' && m.includes(d.brandName.toLowerCase())) || (d.genericName !== '' && m.includes(d.genericName.toLowerCase()))) ?? null
}

// ── C2 Next Service: anchor = Last Service when set, else Start of Care; interval in days ───────
export function calcNextService(s: Pick<PcwSec1, 'startOfCare' | 'dateOfLastService' | 'nextServiceIntervalDays'>): { date: string; anchor: 'last_service' | 'start_of_care' | '' } {
  const interval = parseInt(s.nextServiceIntervalDays, 10)
  if (!interval || interval <= 0) return { date: '', anchor: '' }
  const anchorVal = s.dateOfLastService || s.startOfCare
  if (!anchorVal) return { date: '', anchor: '' }
  const d = new Date(anchorVal.slice(0, 10) + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return { date: '', anchor: '' }
  d.setUTCDate(d.getUTCDate() + interval)
  return { date: d.toISOString().slice(0, 10), anchor: s.dateOfLastService ? 'last_service' : 'start_of_care' }
}
export const pcwIsoToday = (): string => new Date().toISOString().slice(0, 10)

/** asp-pot simpleHash — skips an AI re-run when the prompt input is unchanged. */
export function pcwSimpleHash(text: string): string {
  let hash = 0
  for (let i = 0; i < text.length; i++) { hash = ((hash << 5) - hash) + text.charCodeAt(i); hash |= 0 }
  return 'h' + Math.abs(hash).toString(36)
}

/** Common lab test names (asp-pot commonLabs; display names, no LOINC). */
export const PCW_COMMON_LABS: string[] = [
  'TB screening (IGRA/QuantiFERON)', 'TB screening (PPD/skin test)', 'Hepatitis B panel (HBsAg, anti-HBc, anti-HBs)', 'Hepatitis C antibody', 'JCV antibody',
  'CBC with differential', 'Hemoglobin', 'Hematocrit', 'WBC', 'Platelet count', 'ANC (Absolute Neutrophil Count)',
  'BMP (Basic Metabolic Panel)', 'CMP (Comprehensive Metabolic Panel)', 'Serum Creatinine', 'BUN', 'eGFR', 'Sodium', 'Potassium',
  'AST', 'ALT', 'ALP', 'Total Bilirubin', 'Albumin', 'INR',
  'CRP', 'ESR', 'RF (Rheumatoid Factor)', 'Anti-CCP', 'ANA', 'Anti-dsDNA', 'Complement C3', 'Complement C4',
  'Immunoglobulin G (IgG)', 'Immunoglobulin A (IgA)', 'Immunoglobulin M (IgM)',
  'Beta-hCG (pregnancy)', 'Varicella titer', 'Measles titer',
  'Lipid panel', 'HbA1c', 'TSH', 'Free T4',
  'Urinalysis', 'Urine protein/creatinine ratio',
  'Chest X-ray', 'MRI Brain',
  'TPMT genotype', 'HLA-B*5701', 'G6PD',
]

/** Surveyor export (asp-pot GET /api/pcw/:id/export → pcw_data.json): the record + patient summary as
 *  a JSON download built in the browser. The three PDFs (DRR / dispense / care plan) have no OwlRx
 *  renderer yet — see the GAP list in PotPcw.tsx. */
export function downloadPcwJson(filename: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ── Unclaimed POT pool on the classic dashboard (GET carerx/pot/classic-dashboard → `unclaimed`) ──
// A visit the pharmacy starts from CareRx "Plan of Treatment → Start POT" sits as status 'scheduled'
// with nurse_id NULL; the dashboard scoping (own + shared) hid it from EVERY nurse until an admin
// reassigned it, while the CareRx side had the Nurse POT queue + Claim. The backend now returns that
// pool for NURSE actors as `unclaimed` (same row shape as in_progress); staff/admin get an empty array
// because for them the pool is already inside `in_progress` (Nurse "—", Reassign… in the ⋯ menu).
// Both additions are TypeScript declaration merges onto the Dashboard block above — the existing
// declarations are untouched and every consumer of PotDashboard / PotDashboardRow sees the new fields.
export interface PotDashboard {
  /** Nurse actors: scheduled visits in the pharmacy with no nurse yet. Absent / empty for staff/admin and older backends. */
  unclaimed?: PotDashboardRow[]
}
export interface PotDashboardRow {
  /** yyyy-mm-dd (mig 334: NULL = not scheduled yet); display through formatUsDate. */
  visit_date?: string | null
}
/** What PUT carerx/visits/{id}/claim (client.ts claimCareRxPot) actually returns: the claim succeeds
 *  with open staffing blockers and names them in `note` (CareRxPotFunctions.Claim, B-2). client.ts
 *  types only { id, status }; assigning its result to this type needs no cast (extra members are optional). */
export interface PotClaimResult {
  id: string
  status: string
  nurse_id?: string | null
  agency_id?: string | null
  open_blockers?: string[]
  note?: string | null
}

// ── Instrument of the row's form (GET carerx/pot/classic-dashboard → `schema_code`, D-POT-5 P1) ──
// Two instruments share one key space (D-POT-5 §2.1): 'pot_classic_v1' (POT classic) and 'pot_v1'
// (OwlRx Plan of Treatment, v2). The backend joins carerx_pot_form_schema through the latest form's
// schema_id so a dashboard can route Resume / View to the renderer the form was authored in; NULL
// when the visit has no form yet. Declaration merge onto the Dashboard block — existing declarations
// untouched, older backends simply omit the field.
export interface PotDashboardRow {
  /** 'pot_classic_v1' | 'pot_v1' | a pharmacy-defined code; null when no form exists for the visit. */
  schema_code?: string | null
}
